import {
  buildQueryAuthIdentity,
  queryAuthIdentitiesEqual,
  type QueryAuthIdentity,
} from '../../../providers/query-auth-identity';
import type { AuthUserProfile } from '@/stores/auth-store';

export function isSessionEventAuthHint(event: string): boolean {
  return event === 'INITIAL_SESSION';
}

export function isSupersededAuthResolution(
  generationAtStart: number,
  currentGeneration: number,
): boolean {
  return generationAtStart !== currentGeneration;
}

/**
 * Admit an SSR auth profile only when it belongs to the current realm actor.
 * Transient store nulls compare against the last committed non-null identity so
 * B→null cannot re-admit a previous navigation's A seed. First boot (no store
 * actor and no committed realm identity) still admits the matching SSR seed.
 */
export function admitServerAuthSeed(
  serverUser: AuthUserProfile | null | undefined,
  storeUser: AuthUserProfile | null,
  lastCommittedIdentity: QueryAuthIdentity | null = null,
): AuthUserProfile | null {
  if (!serverUser) return null;

  const serverIdentity = buildQueryAuthIdentity(serverUser);
  if (!serverIdentity) return null;

  const compareIdentity = buildQueryAuthIdentity(storeUser) ?? lastCommittedIdentity;
  if (!compareIdentity) return serverUser;
  return queryAuthIdentitiesEqual(serverIdentity, compareIdentity) ? serverUser : null;
}

export function admitServerShellSeed<T>(
  serverSeed: T | null | undefined,
  serverUser: AuthUserProfile | null | undefined,
  storeUser: AuthUserProfile | null,
  lastCommittedIdentity: QueryAuthIdentity | null = null,
): T | undefined {
  if (serverSeed == null) return undefined;
  if (!serverUser) {
    if (storeUser || lastCommittedIdentity) return undefined;
    return serverSeed;
  }
  if (!admitServerAuthSeed(serverUser, storeUser, lastCommittedIdentity)) return undefined;
  return serverSeed;
}

export function bootRealmSeedConflictsWithStoreActor(
  storeIdentity: QueryAuthIdentity,
  seedUser: AuthUserProfile | null,
): boolean {
  if (!seedUser) return false;
  const seedIdentity = buildQueryAuthIdentity(seedUser);
  if (!seedIdentity) return false;
  return !queryAuthIdentitiesEqual(seedIdentity, storeIdentity);
}
